import { useEffect, useState } from "react";
import { Download, Wallet, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import IncomeChart from "@/components/admin/IncomeChart";
import ResendInvoiceButton from "@/components/admin/ResendInvoiceButton";
import CreditNoteButton from "@/components/admin/CreditNoteButton";

const thisMonth = () => new Date().toLocaleDateString("en-CA", { timeZone: "Indian/Mauritius" }).slice(0, 7);

export default function IncomeSummary() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    setData(null);
    api.get("/admin/income", { params: { month } }).then(({ data }) => setData(data)).catch((e) => { setData({ payments: [], total: 0, count: 0 }); toast.error(formatError(e.response?.data?.detail) || "Could not load income"); });
  }, [month, refresh]);

  const download = async () => {
    setBusy(true);
    try {
      const { data: blob } = await api.get("/admin/income/export", { params: { month }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: `anou-appran-income-${month}.xlsx` }).click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the Excel file.");
    } finally {
      setBusy(false);
    }
  };

  const testInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/test-invoice");
      toast.success(`Test invoice sent to ${data.to} from marieyanna@kmpmoris.com`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send the test invoice.", { duration: 10000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-income">
      <IncomeChart month={month} onPick={(m) => m && setMonth(m)} refreshKey={refresh} />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} data-testid="income-month"
          className="h-11 rounded-xl bg-slate-800 border-2 border-slate-600 px-3 text-white" />
        <div className="bg-slate-800 rounded-2xl border-2 border-emerald-400/60 px-4 py-2 flex items-center gap-2" data-testid="income-total">
          <Wallet size={18} className="text-emerald-300" />
          <span className="text-slate-300 text-sm">{data?.count ?? 0} confirmed payment{data?.count === 1 ? "" : "s"}</span>
          <span className="font-bold text-lg">Rs {(data?.total ?? 0).toLocaleString()}</span>
        </div>
        <button onClick={download} disabled={busy} data-testid="income-download"
          className="ml-auto rounded-full bg-yellow-300 text-slate-900 font-semibold px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-yellow-200 disabled:opacity-60">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download Excel
        </button>
        <button onClick={testInvoice} disabled={busy} data-testid="income-test-invoice"
          className="rounded-full border-2 border-blue-400 text-blue-200 font-semibold px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-blue-500/20 disabled:opacity-60">
          <Mail size={15} /> Send me a test invoice
        </button>
      </div>
      {!data ? <Loader2 className="animate-spin mx-auto" /> : <IncomeTable rows={data.payments} onChanged={() => setRefresh((k) => k + 1)} />}
    </div>
  );
}

function IncomeTable({ rows, onChanged }) {
  const dl = async (r) => {
    try {
      const { data } = await api.get(`/billing/credit-note/${r.credit_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      Object.assign(document.createElement("a"), { href: url, download: `Credit-note-${r.invoice_no.split(" ")[0]}.pdf` }).click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the credit note."); }
  };
  if (!rows.length) return <p className="text-slate-400 text-center py-8" data-testid="income-empty">No confirmed payments this month.</p>;
  return (
    <div className="overflow-x-auto rounded-2xl border-2 border-slate-700">
      <table className="w-full text-sm" data-testid="income-table">
        <thead className="bg-slate-800 text-left text-slate-300">
          <tr>{["Date", "Invoice no.", "Parent", "Children", "Subjects", "Amount", ""].map((h) => <th key={h} className="px-3 py-2">{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.credit_id || r.id} className={`border-t border-slate-700 ${r.type === "credit" ? "bg-rose-500/10" : ""}`} data-testid={`income-row-${r.credit_id || r.id}`}>
              <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
              <td className="px-3 py-2 font-mono">{r.type === "credit" ? <>{r.invoice_no.split(" ")[0]}<div className="text-[11px] text-slate-400">{r.invoice_no.split(" ").slice(1).join(" ")}</div></> : r.invoice_no || "—"}</td>
              <td className="px-3 py-2">{r.parent_name}<div className="text-xs text-slate-400">{r.parent_email}</div></td>
              <td className="px-3 py-2">{r.children}</td>
              <td className="px-3 py-2 text-slate-300 text-xs max-w-[260px]">{r.subjects}</td>
              <td className={`px-3 py-2 font-semibold whitespace-nowrap ${r.amount < 0 ? "text-rose-300" : ""}`}>{r.amount < 0 ? "−" : ""}Rs {Math.abs(r.amount).toLocaleString()}
                {r.note && r.type === "payment" && <div className="text-[11px] font-normal text-amber-200">{r.note}</div>}
                {r.credited > 0 && <div className="text-[11px] font-normal text-rose-300" data-testid={`income-credited-${r.id}`}>Credited Rs {r.credited}</div>}</td>
              <td className="px-3 py-2">{r.type === "credit" ? (
                <button onClick={() => dl(r)} data-testid={`income-credit-pdf-${r.credit_id}`} className="inline-flex items-center gap-1 rounded-full border-2 border-rose-400 text-rose-200 px-3 py-1 text-xs font-semibold"><Download size={12} /> Credit note PDF</button>
              ) : <div className="flex flex-col gap-1 items-start"><ResendInvoiceButton sid={r.id} />
                <CreditNoteButton sub={{ id: r.id, total: r.amount, credited: r.credited, items: r.items, invoice_no: r.invoice_no }} onDone={onChanged} /></div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
